import React, { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import {
  DEFAULT_PEER_REVIEW_TIMER_MINUTES,
  MAX_PEER_REVIEW_TIMER_MINUTES,
  MIN_PEER_REVIEW_TIMER_MINUTES,
} from '../../constants/peerReviewChain.constants';
import VirtualizedDropdown, { type VirtualizedDropdownOption } from '../common/VirtualizedDropdown';
import type {
  PeerReviewLevelFormState,
  PeerReviewerOption,
} from '../../types/peerReviewChain.types';

interface PeerReviewLevelBoxProps {
  level: PeerReviewLevelFormState;
  reviewers: PeerReviewerOption[];
  reservedReviewerIds: ReadonlySet<number>;
  disabled?: boolean;
  showValidation?: boolean;
  onChange: (patch: Partial<PeerReviewLevelFormState>) => void;
}

function parseTimerInput(value: string): number {
  const digits = value.replaceAll(/\D/g, '');
  if (!digits) {
    return 0;
  }
  return Math.min(Number(digits), MAX_PEER_REVIEW_TIMER_MINUTES);
}

function formatReviewerLabel(reviewer: PeerReviewerOption): string {
  return reviewer.subtitle ? `${reviewer.fullName} · ${reviewer.subtitle}` : reviewer.fullName;
}

export default function PeerReviewLevelBox({
  level,
  reviewers,
  reservedReviewerIds,
  disabled = false,
  showValidation = false,
  onChange,
}: Readonly<PeerReviewLevelBoxProps>) {
  const isUnset = level.reviewerId == null;
  const timerInvalid =
    !isUnset &&
    (!level.timerMinutes ||
      level.timerMinutes < MIN_PEER_REVIEW_TIMER_MINUTES ||
      level.timerMinutes > MAX_PEER_REVIEW_TIMER_MINUTES);
  const showTimerError = showValidation && timerInvalid;

  const options = useMemo<VirtualizedDropdownOption[]>(
    () =>
      reviewers
        .filter(
          reviewer => reviewer.id === level.reviewerId || !reservedReviewerIds.has(reviewer.id)
        )
        .map(reviewer => ({
          id: reviewer.id,
          label: formatReviewerLabel(reviewer),
        })),
    [reviewers, reservedReviewerIds, level.reviewerId]
  );

  return (
    <div
      className={[
        'esc-matrix-level',
        'esc-matrix-level--peer-review',
        isUnset ? 'esc-matrix-level--optional' : '',
        showTimerError ? 'esc-matrix-level--error' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="esc-matrix-level-top">
        <span
          className={['esc-matrix-level-index', isUnset ? 'esc-matrix-level-index--optional' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {level.levelNumber}
        </span>
        <span className="esc-matrix-level-role-label">Reviewer</span>
      </div>

      <div className="esc-matrix-level-field">
        <VirtualizedDropdown
          value={level.reviewerId}
          onChange={reviewerId => {
            if (reviewerId == null) {
              onChange({ reviewerId: null, timerMinutes: 0 });
              return;
            }
            onChange({
              reviewerId,
              timerMinutes:
                level.timerMinutes > 0 ? level.timerMinutes : DEFAULT_PEER_REVIEW_TIMER_MINUTES,
            });
          }}
          options={options}
          placeholder="— Not set —"
          noneLabel="— Not set —"
          showNone
          showSearch={false}
          variant="light"
          disabled={disabled}
          truncateTriggerLabel
          mutedDisplayLabels={['— Not set —']}
          ariaLabel={`Reviewer level ${level.levelNumber}`}
          emptyMessage="No radiologists available"
          containerClassName="esc-matrix-peer-select-wrap"
          className="esc-matrix-peer-select"
        />
      </div>

      <div className="esc-matrix-level-field">
        <div className="esc-matrix-timer-wrap">
          <Clock
            size={14}
            strokeWidth={ICON_STROKE_WIDTH}
            className="esc-matrix-timer-icon"
          />
          <input
            type="text"
            inputMode="numeric"
            className={[
              'esc-matrix-timer-input',
              showTimerError ? 'esc-matrix-timer-input--error' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            value={String(level.timerMinutes || 0)}
            disabled={disabled || isUnset}
            onChange={e => onChange({ timerMinutes: parseTimerInput(e.target.value) })}
            aria-label="Minutes to review"
          />
          <span className="esc-matrix-timer-suffix">min to review</span>
        </div>
        {showTimerError && (
          <span className="esc-field-error">
            Timer must be between {MIN_PEER_REVIEW_TIMER_MINUTES} and{' '}
            {MAX_PEER_REVIEW_TIMER_MINUTES} minutes
          </span>
        )}
      </div>
    </div>
  );
}
